import { useState, useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNotification } from "@/providers/NotificationProvider";
import { notifications } from "@/strings";
import { apiAxios } from "@/api/config";

interface UseCompletedHighlightsOptions {
  assignmentId: string;
  documentId: string;
  initialCompletedIds: string[];
  onSuccess?: (ids: string[]) => void;
}

interface UpdateCompletedHighlightsPayload {
  completedHighlightIds: string[];
}

interface UseShareCompletedHighlightsOptions {
  shareToken: string;
  initialCompletedIds: string[];
  onSuccess?: (ids: string[]) => void;
}

type SavingState = "idle" | "debouncing" | "saving" | "saved" | "error";

/**
 * Hook to manage completed highlight IDs with debounced persistence
 *
 * - Stores local state for immediate UI updates
 * - Debounces API calls (500ms) to reduce server load
 * - Shows notifications on errors only
 * - Provides savingState for UX feedback (idle, debouncing, saving, saved, error)
 */
export function useCompletedHighlights({
  assignmentId,
  documentId,
  initialCompletedIds,
  onSuccess,
}: UseCompletedHighlightsOptions) {
  const [completedIds, setCompletedIds] = useState<string[]>(initialCompletedIds);
  const [savingState, setSavingState] = useState<SavingState>("idle");
  const { show } = useNotification();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const savedTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Refs to track latest values for cleanup effect (avoids re-running cleanup on every state change)
  const completedIdsRef = useRef(completedIds);
  const initialCompletedIdsRef = useRef(initialCompletedIds);
  completedIdsRef.current = completedIds;
  initialCompletedIdsRef.current = initialCompletedIds;

  // Mutation to update completed highlights on server
  const updateMutation = useMutation({
    mutationFn: async (payload: UpdateCompletedHighlightsPayload) => {
      setSavingState("saving");
      const response = await apiAxios.patch<{ completedHighlightIds?: string[] }>(
        `/assignments/${assignmentId}/documents/${documentId}/completed-highlights`,
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      setSavingState("saved");
      // Clear "saved" state after 2 seconds
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current);
      }
      savedTimerRef.current = setTimeout(() => {
        setSavingState("idle");
      }, 2000);

      if (onSuccess) {
        onSuccess(data.completedHighlightIds || completedIds);
      }
    },
    onError: (error: unknown) => {
      setSavingState("error");
      const message: string = error instanceof Error ? error.message : notifications.generic.saveError;
      show(message, "error");
    },
  });

  // Handle completed IDs change with debouncing
  const handleCompletedIdsChange = useCallback(
    (newIds: string[]) => {
      // Update local state immediately
      setCompletedIds(newIds);
      setSavingState("debouncing");

      // Clear existing timers
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current);
      }

      // Debounce API call (500ms)
      debounceTimerRef.current = setTimeout(() => {
        updateMutation.mutate({ completedHighlightIds: newIds });
      }, 500);
    },
    // Including completedIds ensures callback is recreated when state changes,
    // preventing stale closures in downstream components
    [updateMutation.mutate, completedIds],
  );

  // Synchronizes local state with external completed IDs when props change
  useEffect(() => {
    // Clear any pending updates since we're syncing with new external state
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setCompletedIds(initialCompletedIds);
  }, [initialCompletedIds, assignmentId, documentId]);

  // Cleanup timer on unmount and flush pending updates
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        // Flush the pending update immediately on unmount
        if (completedIdsRef.current !== initialCompletedIdsRef.current) {
          const payload = { completedHighlightIds: completedIdsRef.current };
          const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
          const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
          const url = `${baseUrl}/assignments/${assignmentId}/documents/${documentId}/completed-highlights`;

          // Use fetch with keepalive: true (modern replacement for sendBeacon that supports PATCH and headers)
          // We need to manually construct headers since we're bypassing axios
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
          };

          // Try to get token from storage (standard place for auth store)
          try {
            const authStorage = localStorage.getItem('auth-storage');
            if (authStorage) {
              const parsed = JSON.parse(authStorage) as { state?: { accessToken?: string } };
              if (parsed.state?.accessToken) {
                headers['Authorization'] = `Bearer ${parsed.state.accessToken}`;
              }
            }
          } catch {
            // Ignore error parsing token
          }

          fetch(url, {
            method: 'PATCH',
            headers,
            body: JSON.stringify(payload),
            keepalive: true,
          }).catch(() => {
             // If keepalive fetch fails, try mutation (best effort)
             updateMutation.mutate(payload);
          });
        }
      }
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current);
      }
    };
  }, []);

  return {
    completedIds,
    handleCompletedIdsChange,
    isPending: updateMutation.isPending,
    savingState,
  };
}

/**
 * Variant of useCompletedHighlights for public share links.
 *
 * Uses the share token instead of assignment/document IDs and does not require authentication.
 */
export function useShareCompletedHighlights({
  shareToken,
  initialCompletedIds,
  onSuccess,
}: UseShareCompletedHighlightsOptions) {
  const [completedIds, setCompletedIds] = useState<string[]>(initialCompletedIds);
  const { show } = useNotification();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Refs to track latest values for cleanup effect (avoids re-running cleanup on every state change)
  const completedIdsRef = useRef(completedIds);
  const initialCompletedIdsRef = useRef(initialCompletedIds);
  completedIdsRef.current = completedIds;
  initialCompletedIdsRef.current = initialCompletedIds;

  const updateMutation = useMutation({
    mutationFn: async (payload: UpdateCompletedHighlightsPayload) => {
      const response = await apiAxios.patch<{ completedHighlightIds?: string[] }>(
        `/share/${shareToken}/completed-highlights`,
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Don't update state here - it was already updated optimistically in handleCompletedIdsChange
      // Updating state here causes infinite loop as it triggers re-renders
      if (onSuccess) {
        const nextIds = data.completedHighlightIds ?? completedIds;
        onSuccess(nextIds);
      }
    },
    onError: (error: unknown) => {
      const message: string =
        error instanceof Error ? error.message : notifications.generic.saveError;
      show(message, "error");
    },
  });

  const handleCompletedIdsChange = useCallback(
    (newIds: string[]) => {
      setCompletedIds(newIds);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        updateMutation.mutate({ completedHighlightIds: newIds });
      }, 500);
    },
    // Including completedIds ensures callback is recreated when state changes,
    // preventing stale closures in downstream components
    [updateMutation.mutate, completedIds],
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        // Flush the pending update immediately on unmount
        if (completedIdsRef.current !== initialCompletedIdsRef.current) {
          const payload = { completedHighlightIds: completedIdsRef.current };
          const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
          const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
          const url = `${baseUrl}/share/${shareToken}/completed-highlights`;

          fetch(url, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
            keepalive: true,
          }).catch(() => {
             updateMutation.mutate(payload);
          });
        }
      }
    };
  }, []);

  return {
    completedIds,
    handleCompletedIdsChange,
    isPending: updateMutation.isPending,
  };
}

interface UsePortalCompletedHighlightsOptions {
  portalKey: string;
  documentId: string;
  initialCompletedIds: string[];
  onSuccess?: (ids: string[]) => void;
}

/**
 * Variant of useCompletedHighlights for student portal.
 *
 * Uses the portal key and document ID. Does not require authentication.
 */
export function usePortalCompletedHighlights({
  portalKey,
  documentId,
  initialCompletedIds,
  onSuccess,
}: UsePortalCompletedHighlightsOptions) {
  const [completedIds, setCompletedIds] = useState<string[]>(initialCompletedIds);
  const { show } = useNotification();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Refs to track latest values for cleanup effect (avoids re-running cleanup on every state change)
  const completedIdsRef = useRef(completedIds);
  const initialCompletedIdsRef = useRef(initialCompletedIds);
  completedIdsRef.current = completedIds;
  initialCompletedIdsRef.current = initialCompletedIds;

  const updateMutation = useMutation({
    mutationFn: async (payload: UpdateCompletedHighlightsPayload) => {
      const response = await apiAxios.patch<{ completedHighlightIds?: string[] }>(
        `/portal/${portalKey}/paper/${documentId}/completed-highlights`,
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (onSuccess) {
        const nextIds = data.completedHighlightIds ?? completedIds;
        onSuccess(nextIds);
      }
    },
    onError: (error: unknown) => {
      const message: string =
        error instanceof Error ? error.message : notifications.generic.saveError;
      show(message, "error");
    },
  });

  const handleCompletedIdsChange = useCallback(
    (newIds: string[]) => {
      setCompletedIds(newIds);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        updateMutation.mutate({ completedHighlightIds: newIds });
      }, 500);
    },
    [updateMutation.mutate, completedIds],
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        // Flush the pending update immediately on unmount
        if (completedIdsRef.current !== initialCompletedIdsRef.current) {
          const payload = { completedHighlightIds: completedIdsRef.current };
          const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
          const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
          const url = `${baseUrl}/portal/${portalKey}/paper/${documentId}/completed-highlights`;

          fetch(url, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
            keepalive: true,
          }).catch(() => {
             updateMutation.mutate(payload);
          });
        }
      }
    };
  }, []);

  return {
    completedIds,
    handleCompletedIdsChange,
    isPending: updateMutation.isPending,
  };
}
